import React, { useCallback, useState } from 'react'
import { requestComplianceTextCheck } from '../lib/complianceTextCheck'
import { Button } from './ui/button'

/**
 * @param {object} props
 * @param {() => string} props.getText 편집 중 화면 문구를 반환 (비어 있으면 토스트)
 * @param {string} [props.label]
 * @param {string} [props.className]
 */
export default function ComplianceTextCheckButton({ getText, label = '표현 리스크 점검', className = '' }) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const run = useCallback(async () => {
    const text = typeof getText === 'function' ? getText() : ''
    const body = String(text || '').trim()
    if (!body) {
      try { window?.toast?.show?.('점검할 문구가 없습니다. 소개·문구를 입력한 뒤 다시 시도해 주세요.', 'error') } catch { }
      return
    }
    setLoading(true)
    setResult(null)
    try {
      const out = await requestComplianceTextCheck(body)
      if (!out.ok && out.error === 'empty') {
        try { window?.toast?.show?.('점검할 문구가 없습니다. 소개·문구를 입력한 뒤 다시 시도해 주세요.', 'error') } catch { }
        return
      }
      if (!out.ok) {
        try {
          window?.toast?.show?.(out.error === 'parse' ? '점검 결과를 해석할 수 없습니다. 잠시 후 다시 시도해 주세요.' : String(out.error), 'error')
        } catch { }
        return
      }
      setResult(out.result)
      setOpen(true)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '점검에 실패했습니다.'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setLoading(false)
    }
  }, [getText])

  return (
    <>
      <button
        type="button"
        disabled={loading}
        onClick={run}
        className={
          className
          || 'rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50'
        }
      >
        {loading ? '점검 중…' : label}
      </button>

      {open && result ? (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-label="표현 리스크 점검 닫기"
          />
          <div className="relative flex min-h-full items-center justify-center px-3 py-10 sm:px-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-xl rounded-2xl border bg-white shadow-xl overflow-hidden" role="dialog" aria-modal="true">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div>
                  <div className="font-bold text-sm sm:text-base">금소법·광고 표현 리스크 점검</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    위험도 {result.riskLevel || 'LOW'} · 점수 {result.score ?? 0}
                  </div>
                </div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setOpen(false)}>닫기</button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-5 space-y-3">
                {Array.isArray(result.findings) && result.findings.length > 0 ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
                    <div className="mb-1 font-semibold">감지된 항목</div>
                    {result.findings.map((f, idx) => (
                      <div key={`risk-finding-${idx}`} className="border-t border-amber-200/70 py-1 first:border-t-0">
                        <strong>{f.keyword}</strong>
                        {' '}
                        · {f.category}: {f.reason}
                        {f.suggestion ? <div className="text-amber-800">제안: {f.suggestion}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    자동 점검 기준에서 특별히 걸린 표현이 없습니다.
                  </div>
                )}
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-1 text-[11px] font-semibold text-slate-500">수정 제안 문구</div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800">{result.safeRewrite || '—'}</p>
                </div>
                {Array.isArray(result.checklist) && result.checklist.length > 0 ? (
                  <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
                    {result.checklist.map((item) => <div key={String(item)}>· {String(item)}</div>)}
                  </div>
                ) : null}
                <div className="rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-[11px] leading-relaxed text-indigo-950">
                  본 결과는 키워드 기반 사전 점검일 뿐이며, 광고·모집 문구에 대한 심의필 발급을 대체하지 않습니다.
                  실제 게재·발송 전에는 소속사 및 매체 준법 기준에 따라 심의·표기 절차를 확인해 주세요.
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="sgezOutline" size="sm" onClick={() => setOpen(false)}>닫기</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
